import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePortalRole } from '../../../contexts/PortalRoleContext';
import { listCampuses } from '../../../api/campusPortalApi';
import {
  createCourseGroup,
  updateCourseGroup,
  getCourseGroup,
  getCourseGroupTypes,
  getRooms,
  YEAR_OPTIONS,
  TERM_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  isSpecialTerm
} from '../../../api/courseGroupPortalApi';
import { listTeachers } from '../../../api/courseGroupTeacherApi';
import { shiftMaterialWeeks } from '../../../api/courseGroupMaterialApi';
import {
  listCourseGroupLessons, deleteLesson, getLessonStatusLabel
} from '../../../api/timetableApi';
import {
  canFilterByCampus,
  canEditAllCourseGroupFields,
  canManageVisibilityRestrictions
} from '../../../utils/portalPermissions';
import SubjectMultiSelect from '../../../components/Portal/SubjectMultiSelect';
import {
  byBestMatch, teacherOptionLabel, anyoneHasAvailability
} from '../../../utils/teacherAvailability';
import * as S from '../Management.styles';
import {
  WeekPreviewContainer, WeekPreviewHeader, WeekPreviewRow, WeekPreviewBadge, WeekPreviewSummary,
  SlotPreviewContainer, SlotPreviewHeader, SlotPreviewSummary, SlotBadge,
  SlotInputRow, SlotInputMain, SlotInputSub,
  SlotSelectionBar, SlotCheckboxLabel, SlotSelectionCount, SlotRowCheckbox
} from './CourseGroupForm.styles';

/**
 * Create or edit one course group, wherever the user happens to be.
 *
 * This was the Course Groups page's own dialog until 2026-09-12. The timetable now needs the
 * same thing — a class is created and edited from the grid rather than by leaving for another
 * screen — and two copies of a form that writes recurrence rules would drift apart in a week.
 * So the form lives here and both screens render it.
 *
 * Everything it needs it fetches itself (campuses, class types, rooms, the teachers assigned to
 * the group), because the timetable has none of that loaded and asking every caller to supply it
 * is how the copy would start again.
 *
 * @param {'create'|'edit'} mode
 * @param {Object} group           the course group being edited; required when mode is 'edit'
 * @param {Object} prefill         for 'create': campusId / dayOfWeek / startTime / endTime /
 *                                 room, so a class drawn from an empty timetable cell opens
 *                                 already sitting in that cell
 * @param {Function} onSaved       called with the saved group once the write succeeds
 * @param {Function} onClose
 */

/**
 * Calculate all weeks from start date to finish date, accounting for holidays.
 * Week starts from Monday (even if startDate is mid-week).
 */
export const calculateWeeksPreview = (startDate, finishDate, holidayWeeksStr, offset = 0) => {
  if (!startDate || !finishDate) return { weeks: [], totalWeeks: 0, totalTeachingWeeks: 0 };

  const holidayWeeks = holidayWeeksStr
    ? holidayWeeksStr.split(',').map(w => parseInt(w.trim())).filter(w => !isNaN(w))
    : [];

  const start = new Date(startDate);
  const finish = new Date(finishDate);

  if (isNaN(start.getTime()) || isNaN(finish.getTime())) {
    return { weeks: [], totalWeeks: 0, totalTeachingWeeks: 0 };
  }

  // Week 1 starts on the course start date itself (+ offset days), not the
  // Monday of that week — matches calculate_week_info / calculate_all_weeks.
  const weekStart = new Date(start);
  weekStart.setDate(weekStart.getDate() + (parseInt(offset) || 0));

  const weeks = [];
  let weekNum = 1;
  let currentWeekStart = new Date(weekStart);

  while (currentWeekStart <= finish) {
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);

    const isHoliday = holidayWeeks.includes(weekNum);

    weeks.push({
      week: weekNum,
      startDate: currentWeekStart.toISOString().split('T')[0],
      endDate: currentWeekEnd.toISOString().split('T')[0],
      isHoliday
    });

    weekNum++;
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);

    // Safety limit
    if (weekNum > 52) break;
  }

  const totalTeachingWeeks = weeks.filter(w => !w.isHoliday).length;

  return {
    weeks,
    totalWeeks: weeks.length,
    totalTeachingWeeks
  };
};

const EMPTY_SCHEDULE = { dayOfWeek: 1, startTime: '16:00', endTime: '17:30', subjectIds: [], teacherId: '' };

/**
 * The subjects taught in one booking.
 *
 * A booking covers one subject when the block is split by time and several when it is not,
 * so this is a list. `subjectId` is read as a fallback for rows loaded before the migration.
 */
export const subjectIdsOf = (row) => {
  const ids = (row.subjectIds || []).filter(Boolean);
  if (ids.length) return ids;
  return row.subjectId ? [row.subjectId] : [];
};

const isValidTimeFormat = (time) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time || '');

const isEndTimeAfterStartTime = (startTime, endTime) => {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) return false;
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  return endH * 60 + endM > startH * 60 + startM;
};

/** The first error that stops a save, or null. */
export const validateSchedules = (schedules) => {
  for (let i = 0; i < schedules.length; i++) {
    const schedule = schedules[i];

    // Skip empty schedules
    if (!schedule.dayOfWeek && !schedule.startTime && !schedule.endTime) {
      continue;
    }

    if (!schedule.dayOfWeek || schedule.dayOfWeek < 1 || schedule.dayOfWeek > 7) {
      return `Schedule ${i + 1}: Please select a valid day of week`;
    }
    if (!isValidTimeFormat(schedule.startTime)) {
      return `Schedule ${i + 1}: Invalid start time format (use HH:MM)`;
    }
    if (!isValidTimeFormat(schedule.endTime)) {
      return `Schedule ${i + 1}: Invalid end time format (use HH:MM)`;
    }
    if (!isEndTimeAfterStartTime(schedule.startTime, schedule.endTime)) {
      return `Schedule ${i + 1}: End time must be after start time`;
    }
  }
  return null;
};

/**
 * Bookings that cannot both be true, or that read like a mistyped time.
 *
 * None of this is illegal, so none of it blocks a save — a campus can legitimately run
 * something unusual. But two of one group's bookings sitting on top of each other means one
 * teacher in two places at once, and a class recorded as running for half a day is a single
 * mistyped field. Both went unnoticed for a whole term because nothing ever said anything:
 * the longest real class here is 105 minutes, yet the data held bookings ending at 22:30 and
 * 23:00, and one starting at 05:00. The person who can fix that is the one on this form.
 */
const scheduleWarnings = (schedules) => {
  const mins = (t) => {
    if (!isValidTimeFormat(t)) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const warnings = [];
  const rows = (schedules || [])
    .map((s, i) => ({ ...s, label: `Schedule ${i + 1}`, from: mins(s.startTime), to: mins(s.endTime) }))
    .filter(s => s.from !== null && s.to !== null);

  rows.forEach(row => {
    const length = row.to - row.from;
    if (length > 240) {
      warnings.push(
        `${row.label} runs for ${(length / 60).toFixed(1)} hours `
        + `(${row.startTime}–${row.endTime}). Is that right?`
      );
    }
  });

  for (let a = 0; a < rows.length; a += 1) {
    for (let b = a + 1; b < rows.length; b += 1) {
      if (String(rows[a].dayOfWeek) !== String(rows[b].dayOfWeek)) continue;
      if (rows[a].from < rows[b].to && rows[b].from < rows[a].to) {
        warnings.push(
          `${rows[a].label} (${rows[a].startTime}–${rows[a].endTime}) and `
          + `${rows[b].label} (${rows[b].startTime}–${rows[b].endTime}) are on the same day `
          + `and overlap. Did you mean them to run one after the other?`
        );
      }
    }
  }
  return warnings;
};

/** Show anything odd about the schedule and let the user decide. Returns false to stop. */
export const confirmScheduleWarnings = (schedules) => {
  const warnings = scheduleWarnings(schedules);
  if (!warnings.length) return true;
  const bullets = warnings.map(w => `• ${w}`).join('\n');
  return window.confirm(`Please check the schedule:\n\n${bullets}\n\nSave anyway?`);
};

/**
 * Keep assigned materials on the same teaching week after the holiday weeks change.
 *
 * Adding a holiday week pushes every later session back by one; without this the material
 * assigned to "week 6" would silently become a different lesson's, and someone would have to
 * re-correct the whole sequence by hand in Manage Materials.
 */
const shiftMaterialsForHolidayChange = async (group, updates, isSpecial) => {
  if (isSpecial || updates.holidayWeeks === undefined) return;
  const oldWeeks = group.holidayWeeks || [];
  const newWeeks = updates.holidayWeeks;
  const oldSorted = [...oldWeeks].map(Number).sort((a, b) => a - b);
  const newSorted = [...newWeeks].map(Number).sort((a, b) => a - b);
  if (JSON.stringify(oldSorted) === JSON.stringify(newSorted)) return;

  try {
    const shiftResult = await shiftMaterialWeeks(group.id, oldWeeks, newWeeks);
    if (shiftResult.shiftedCount > 0) {
      alert(`Holiday weeks updated. Auto-shifted ${shiftResult.shiftedCount} material assignment(s) to keep their teaching week order.`);
    }
    if (shiftResult.overflowCount > 0) {
      const names = shiftResult.overflow.map(o => o.name).filter(Boolean).join(', ');
      alert(
        `${shiftResult.overflowCount} material assignment(s) could not shift forward because the course now ends before their session`
        + (names ? `:\n${names}` : '.')
        + `\n\nExtend the course's finish date, then re-save the holiday weeks (or re-assign these in Manage Materials).`
      );
    }
  } catch (shiftErr) {
    console.error('Error shifting material weeks:', shiftErr);
    alert('Course group saved, but auto-shifting material weeks failed: ' + shiftErr.message + '\nPlease check Manage Materials manually.');
  }
};

const CourseGroupFormModal = ({
  mode = 'create',
  group = null,
  prefill = {},
  onSaved,
  onClose
}) => {
  const { currentRole, currentUserCampusIds } = usePortalRole();
  const canManageCourseVisibility = canManageVisibilityRestrictions(currentRole);
  const isEdit = mode === 'edit';
  // An admin may only move the offset of an existing group; creating one is gated by the
  // caller, so the restricted branch only ever applies to an edit.
  const canEditAllFields = !isEdit || canEditAllCourseGroupFields(currentRole);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [campuses, setCampuses] = useState([]);
  const [courseGroupTypes, setCourseGroupTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [lessonExceptions, setLessonExceptions] = useState([]);
  const [selectedSlotIndices, setSelectedSlotIndices] = useState(new Set());

  const [formData, setFormData] = useState(() => {
    if (isEdit && group) {
      return {
        typeId: group.typeId || '',
        title: group.title || '',
        subjects: group.subjects || [],
        room: group.room || '',
        year: group.year !== undefined ? group.year : '',
        term: group.term !== undefined ? group.term : '',
        startDate: group.startDate || '',
        finishDate: group.finishDate || '',
        offset: group.offset || 0,
        holidayWeeks: (group.holidayWeeks || []).join(', '),
        // Spread first: a booking carries fields this form never shows — scheduleId, which
        // materials and lesson overrides point at, plus reliefAssignments. Listing only the
        // edited fields here silently dropped them on every save.
        schedules: (group.schedules?.length > 0 ? group.schedules : [EMPTY_SCHEDULE]).map(s => ({
          ...s,
          dayOfWeek: s.dayOfWeek || 1,
          startTime: s.startTime || '16:00',
          endTime: s.endTime || '17:30',
          subjectIds: subjectIdsOf(s),
          teacherId: s.teacherId || ''
        })),
        slots: (group.slots || []).map(s => ({
          ...s,
          slotIndex: s.slotIndex,
          date: s.date || '',
          startTime: s.startTime || '09:00',
          endTime: s.endTime || '12:00',
          subjectIds: subjectIdsOf(s),
          teacherId: s.teacherId || ''
        })),
        campusId: group.campusId || '',
        creditPerClass: group.creditPerClass || 0,
        visibleToCampuses: group.visibleToCampuses || []
      };
    }
    // Creating from a timetable cell: the cell already says day, time, room and campus, so the
    // form opens sitting in it rather than asking the user to retype what they just clicked.
    return {
      typeId: '',
      title: '',
      subjects: [],
      room: '',
      year: '',
      term: '',
      startDate: '',
      finishDate: '',
      offset: 0,
      holidayWeeks: '',
      schedules: [{
        ...EMPTY_SCHEDULE,
        dayOfWeek: prefill.dayOfWeek || EMPTY_SCHEDULE.dayOfWeek,
        startTime: prefill.startTime || EMPTY_SCHEDULE.startTime,
        endTime: prefill.endTime || EMPTY_SCHEDULE.endTime,
        room: prefill.room || ''
      }],
      slots: [],
      campusId: prefill.campusId || (currentUserCampusIds.length === 1 ? currentUserCampusIds[0] : ''),
      creditPerClass: 0,
      visibleToCampuses: []
    };
  });

  // Reference data. Loaded here rather than passed in, so the timetable does not have to know
  // what a course group form happens to need today.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [campusList, types] = await Promise.all([
          listCampuses(),
          getCourseGroupTypes()
        ]);
        if (cancelled) return;
        setCampuses(campusList || []);
        setCourseGroupTypes(types || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the form');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Teachers and the timetable's one-off changes only exist once the group does.
  useEffect(() => {
    if (!isEdit || !group?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const teachers = await listTeachers(group.id);
        if (!cancelled) setAssignedTeachers(teachers || []);
      } catch (err) {
        console.error('Error loading assigned teachers:', err);
        if (!cancelled) setAssignedTeachers([]);
      }
      // Fails soft on purpose: the exception list is context, not part of the edit. A campus
      // that has never opened the timetable simply has none, and a lookup that errors must not
      // stop someone editing the class.
      try {
        const lessons = await listCourseGroupLessons(group.id);
        if (!cancelled) setLessonExceptions(lessons || []);
      } catch (err) {
        console.error('Error loading lesson exceptions:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, group?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!formData.campusId) {
        setRooms([]);
        return;
      }
      try {
        const roomsData = await getRooms(formData.campusId);
        if (!cancelled) setRooms(roomsData || []);
      } catch (err) {
        console.error('Error loading rooms:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.campusId]);

  const weekPreview = useMemo(
    () => calculateWeeksPreview(formData.startDate, formData.finishDate, formData.holidayWeeks, formData.offset),
    [formData.startDate, formData.finishDate, formData.holidayWeeks, formData.offset]
  );

  const flattenedSubjects = useMemo(() => {
    const subjects = [];
    (formData.subjects || []).forEach(parent => {
      subjects.push({ id: parent.subjectId, name: parent.subjectName, isChild: false });
      (parent.children || []).forEach(child => {
        subjects.push({ id: child.subjectId, name: `  ${child.subjectName}`, isChild: true, parentId: parent.subjectId });
      });
    });
    return subjects;
  }, [formData.subjects]);

  const getTeachersForSubject = useCallback((subjectId) => {
    if (!subjectId) return [];
    return assignedTeachers.filter(teacher => (teacher.subjectIds || []).includes(subjectId));
  }, [assignedTeachers]);

  /**
   * Teachers who cover every subject of a booking, which is what standing in front of it means.
   *
   * Ordered by who is actually free at that hour, so the obvious choice is the first one. The
   * ones who are not free stay in the list: this form records what the term does, and somebody
   * has to be put in front of a class even when the hour suits nobody.
   */
  const getTeachersForSubjects = useCallback((subjectIds, schedule) => {
    if (!subjectIds || !subjectIds.length) return [];
    const eligible = assignedTeachers.filter(teacher =>
      subjectIds.every(sid => (teacher.subjectIds || []).includes(sid))
    );
    const need = schedule ? {
      dayOfWeek: schedule.dayOfWeek,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      year: formData.year,
      subjectIds
    } : null;
    if (!need) return eligible;
    return [...eligible].sort(byBestMatch(need, t => t.teacherName || ''));
  }, [assignedTeachers, formData.year]);

  /** Stay quiet about availability until somebody on this group has filled theirs in. */
  const showAvailabilityHints = useMemo(
    () => anyoneHasAvailability(assignedTeachers),
    [assignedTeachers]
  );

  // ---- schedules ----

  const addSchedule = () => setFormData(prev => ({
    ...prev, schedules: [...prev.schedules, { ...EMPTY_SCHEDULE }]
  }));

  const removeSchedule = (index) => setFormData(prev => ({
    ...prev, schedules: prev.schedules.filter((_, i) => i !== index)
  }));

  const updateSchedule = (index, field, value) => setFormData(prev => ({
    ...prev,
    schedules: prev.schedules.map((s, i) =>
      i === index ? { ...s, [field]: field === 'dayOfWeek' ? parseInt(value) : value } : s
    )
  }));

  /** Add or remove one subject from a booking. */
  const toggleScheduleSubject = (index, subjectId) => {
    setFormData(prev => ({
      ...prev,
      schedules: prev.schedules.map((schedule, i) => {
        if (i !== index) return schedule;
        const current = subjectIdsOf(schedule);
        const next = current.includes(subjectId)
          ? current.filter(id => id !== subjectId)
          : [...current, subjectId];
        // A teacher is validated against every subject of the booking, so dropping the last
        // one leaves a teacher the server would refuse.
        return { ...schedule, subjectIds: next, teacherId: next.length ? schedule.teacherId : '' };
      })
    }));
  };

  const autoAssignTeachersToSchedules = () => {
    if (assignedTeachers.length === 0) {
      alert('No teachers assigned to this course group. Please assign teachers first.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      schedules: prev.schedules.map(schedule => {
        const scheduleSubjects = subjectIdsOf(schedule);
        if (!scheduleSubjects.length || schedule.teacherId) return schedule;
        const teacher = assignedTeachers.find(t =>
          scheduleSubjects.every(sid => (t.subjectIds || []).includes(sid))
        );
        return teacher ? { ...schedule, teacherId: teacher.teacherId } : schedule;
      })
    }));
  };

  // ---- slots (special term) ----

  const addSlot = () => {
    const nextIndex = Math.max(0, ...formData.slots.map(s => s.slotIndex || 0)) + 1;
    setFormData(prev => ({
      ...prev,
      slots: [...prev.slots, {
        slotIndex: nextIndex,
        date: prev.startDate || '',
        startTime: '09:00',
        endTime: '12:00',
        subjectId: '',
        teacherId: ''
      }]
    }));
  };

  const removeSlot = (index) => setFormData(prev => ({
    ...prev, slots: prev.slots.filter((_, i) => i !== index)
  }));

  const updateSlot = (index, field, value) => setFormData(prev => ({
    ...prev, slots: prev.slots.map((s, i) => (i === index ? { ...s, [field]: value } : s))
  }));

  const toggleSlotSelection = (idx) => setSelectedSlotIndices(prev => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });

  const selectAllSlots = () => setSelectedSlotIndices(new Set(formData.slots.map((_, idx) => idx)));
  const clearSlotSelection = () => setSelectedSlotIndices(new Set());

  const copySelectedSlots = () => {
    if (selectedSlotIndices.size === 0) return;
    const selected = Array.from(selectedSlotIndices).sort((a, b) => a - b);
    const currentMaxIndex = Math.max(0, ...formData.slots.map(s => s.slotIndex || 0));

    const newSlots = selected.map((idx, i) => {
      const original = formData.slots[idx];
      let newDate = original.date;
      if (original.date) {
        const dateObj = new Date(original.date);
        dateObj.setDate(dateObj.getDate() + 1);
        newDate = dateObj.toISOString().split('T')[0];
      }
      return { ...original, slotIndex: currentMaxIndex + i + 1, date: newDate };
    });

    setFormData(prev => ({ ...prev, slots: [...prev.slots, ...newSlots] }));
    setSelectedSlotIndices(new Set());
  };

  const autoAssignTeachersToSlots = () => {
    if (assignedTeachers.length === 0) {
      alert('No teachers assigned to this course group. Please assign teachers first.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      slots: prev.slots.map(slot => {
        if (!slot.subjectId || slot.teacherId) return slot;
        const teacher = assignedTeachers.find(t => (t.subjectIds || []).includes(slot.subjectId));
        return teacher ? { ...slot, teacherId: teacher.teacherId } : slot;
      })
    }));
  };

  /**
   * Undo one timetable exception, so that week goes back to what the schedule above says.
   *
   * Deleting the LessonPortal row is the whole operation: the occurrence it was hiding is
   * generated again on the next read, because instances are computed rather than stored.
   */
  const handleRestoreException = async (lesson) => {
    setSaving(true);
    try {
      await deleteLesson(lesson.id);
      setLessonExceptions(prev => prev.filter(item => item.id !== lesson.id));
    } catch (err) {
      setError(err.message || 'Could not restore that week');
    } finally {
      setSaving(false);
    }
  };

  // ---- saving ----

  const handleSave = async () => {
    setError('');
    const isSpecial = isSpecialTerm(formData.term);

    if (!isEdit) {
      if (!formData.typeId || !formData.title
        || formData.year === '' || formData.year === undefined
        || formData.term === '' || formData.term === undefined
        || !formData.startDate || !formData.finishDate || !formData.campusId) {
        setError('Please fill in all required fields');
        return;
      }
    }

    if (!isSpecial && formData.schedules && formData.schedules.length > 0) {
      const scheduleError = validateSchedules(formData.schedules);
      if (scheduleError) {
        setError(scheduleError);
        return;
      }
      if (!confirmScheduleWarnings(formData.schedules)) return;
    }

    setSaving(true);
    try {
      const holidayWeeksArray = formData.holidayWeeks
        ? formData.holidayWeeks.split(',').map(w => parseInt(w.trim())).filter(w => !isNaN(w))
        : [];

      let saved;
      if (!isEdit) {
        const createData = {
          typeId: formData.typeId,
          title: formData.title,
          subjects: formData.subjects,
          room: formData.room,
          year: parseInt(formData.year),
          term: isSpecial ? 0 : parseInt(formData.term),
          startDate: formData.startDate,
          finishDate: formData.finishDate,
          campusId: formData.campusId,
          creditPerClass: parseFloat(formData.creditPerClass) || 0
        };
        if (canManageCourseVisibility) {
          createData.visibleToCampuses = formData.visibleToCampuses || [];
        }
        if (isSpecial) {
          createData.slots = formData.slots.filter(s => s.date && s.startTime && s.endTime);
        } else {
          createData.offset = parseInt(formData.offset) || 0;
          createData.holidayWeeks = holidayWeeksArray;
          createData.schedules = formData.schedules.filter(s => s.dayOfWeek && s.startTime && s.endTime);
        }
        saved = await createCourseGroup(createData);
      } else {
        const updates = {};
        if (canEditAllFields) {
          if (formData.typeId) updates.typeId = formData.typeId;
          if (formData.title) updates.title = formData.title;
          if (formData.subjects !== undefined) updates.subjects = formData.subjects;
          if (formData.room !== undefined) updates.room = formData.room;
          if (formData.year !== undefined && formData.year !== '') updates.year = parseInt(formData.year);
          if (formData.term !== undefined && formData.term !== '') updates.term = isSpecial ? 0 : parseInt(formData.term);
          if (formData.startDate) updates.startDate = formData.startDate;
          if (formData.finishDate) updates.finishDate = formData.finishDate;
          if (formData.creditPerClass !== undefined) updates.creditPerClass = parseFloat(formData.creditPerClass) || 0;
          if (isSpecial) {
            updates.slots = formData.slots.filter(s => s.date && s.startTime && s.endTime);
            // A group that became special-term has no weekly rule left to honour; leaving the
            // old schedules behind would keep generating classes it no longer runs.
            updates.schedules = [];
            updates.holidayWeeks = [];
          } else {
            if (formData.holidayWeeks !== undefined) updates.holidayWeeks = holidayWeeksArray;
            if (formData.schedules) {
              updates.schedules = formData.schedules.filter(s => s.dayOfWeek && s.startTime && s.endTime);
            }
          }
        }
        // Offset only means anything to a weekly recurrence.
        if (!isSpecial) updates.offset = parseInt(formData.offset) || 0;
        if (canManageCourseVisibility) {
          updates.visibleToCampuses = formData.visibleToCampuses || [];
        }
        await updateCourseGroup(group.id, updates);
        await shiftMaterialsForHolidayChange(group, updates, isSpecial);
        // Read it back rather than merging locally: the server stamps scheduleIds onto any
        // booking that arrived without one, and a caller that redraws from the merged object
        // would be holding bookings with no identity.
        saved = await getCourseGroup(group.id);
      }

      clearSlotSelection();
      if (onSaved) await onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.message || (isEdit ? 'Could not save the changes' : 'Could not create the course group'));
      setSaving(false);
    }
  };

  const close = () => {
    clearSlotSelection();
    onClose();
  };

  const campusOptions = canFilterByCampus(currentRole)
    ? campuses
    : campuses.filter(c => currentUserCampusIds.includes(c.id));

  return (
    <S.ModalOverlay>
      <S.Modal style={{ maxWidth: '800px' }}>
        <S.ModalHeader>
          <h3>{isEdit ? 'Edit Course Group' : 'Create Course Group'}</h3>
          <S.CloseButton onClick={close}>&times;</S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          {error && (
            <div style={{
              padding: '10px 12px', marginBottom: '12px', borderRadius: '8px',
              background: '#fee2e2', color: '#b91c1c', fontSize: '13px'
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : canEditAllFields ? (
            <>
              <S.FormGroup>
                <label>Type {isEdit ? '' : '*'}</label>
                <S.FilterSelect
                  value={formData.typeId}
                  onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                >
                  <option value="">Select Type</option>
                  {courseGroupTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.displayName} {type.capacityLimit ? `(Max ${type.capacityLimit} students)` : '(No limit)'}
                    </option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              <S.FormGroup>
                <label>Title *</label>
                <S.Input
                  type="text"
                  placeholder="Enter course group title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Subjects</label>
                <SubjectMultiSelect
                  value={formData.subjects}
                  onChange={(subjects) => setFormData({ ...formData, subjects })}
                  placeholder="Select subjects..."
                />
              </S.FormGroup>

              <S.FormGroup>
                <label>Room</label>
                <S.Input
                  type="text"
                  list="course-group-form-rooms"
                  placeholder="Select or enter room"
                  value={formData.room}
                  onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                />
                <datalist id="course-group-form-rooms">
                  {rooms.map(r => <option key={r} value={r} />)}
                </datalist>
              </S.FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <S.FormGroup>
                  <label>Year *</label>
                  <S.FilterSelect
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  >
                    <option value="">Select Year</option>
                    {YEAR_OPTIONS.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                  </S.FilterSelect>
                </S.FormGroup>
                <S.FormGroup>
                  <label>Term *</label>
                  <S.FilterSelect
                    value={formData.term}
                    onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                  >
                    <option value="">Select Term</option>
                    {TERM_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </S.FilterSelect>
                </S.FormGroup>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <S.FormGroup>
                  <label>Start Date *</label>
                  <S.Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <label>Finish Date *</label>
                  <S.Input
                    type="date"
                    value={formData.finishDate}
                    onChange={(e) => setFormData({ ...formData, finishDate: e.target.value })}
                  />
                </S.FormGroup>
              </div>

              <S.FormGroup>
                <label>Campus *</label>
                <S.FilterSelect
                  value={formData.campusId}
                  onChange={(e) => setFormData({ ...formData, campusId: e.target.value })}
                  // The campus of an existing group is not something this form moves: its
                  // students, billing and rooms all live there.
                  disabled={isEdit || (!canFilterByCampus(currentRole) && currentUserCampusIds.length <= 1)}
                >
                  {campusOptions.length !== 1 && <option value="">Select Campus</option>}
                  {(isEdit ? campuses.filter(c => c.id === formData.campusId) : campusOptions)
                    .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </S.FilterSelect>
              </S.FormGroup>

              <S.FormGroup>
                <label>Hourly Rate ($)</label>
                <S.Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.creditPerClass}
                  onChange={(e) => setFormData({ ...formData, creditPerClass: e.target.value })}
                />
              </S.FormGroup>

              {canManageCourseVisibility && (
                <S.FormGroup>
                  <label>
                    Visible To Campuses&nbsp;
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: '#b45309',
                      background: '#fef3c7', padding: '2px 8px', borderRadius: '10px'
                    }}>
                      🔒 System Admin Only
                    </span>
                  </label>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    Leave empty for default visibility. When set, this course group (and its billing/credits) is only visible to admin/manager of the chosen campuses; director is always blocked.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {campuses.map(c => {
                      const selected = (formData.visibleToCampuses || []).includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            const current = formData.visibleToCampuses || [];
                            setFormData({
                              ...formData,
                              visibleToCampuses: selected
                                ? current.filter(id => id !== c.id)
                                : [...current, c.id]
                            });
                          }}
                          style={{
                            padding: '4px 10px', borderRadius: '8px', fontSize: '12px',
                            fontWeight: 500, border: 'none', cursor: 'pointer',
                            background: selected ? '#3b82f6' : '#e5e7eb',
                            color: selected ? 'white' : '#374151'
                          }}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </S.FormGroup>
              )}

              {/* Regular term fields */}
              {!isSpecialTerm(formData.term) && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <S.FormGroup>
                      <label>Offset (days)</label>
                      <S.Input
                        type="number"
                        value={formData.offset}
                        onChange={(e) => setFormData({ ...formData, offset: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Holiday Weeks</label>
                      <S.Input
                        type="text"
                        placeholder="e.g., 1, 5, 10"
                        value={formData.holidayWeeks}
                        onChange={(e) => setFormData({ ...formData, holidayWeeks: e.target.value })}
                      />
                    </S.FormGroup>
                  </div>

                  {weekPreview.weeks.length > 0 && (
                    <S.FormGroup>
                      <label>Week Preview</label>
                      <WeekPreviewContainer>
                        <WeekPreviewHeader>
                          <span>Week</span>
                          <span>Start</span>
                          <span>End</span>
                          <span>Status</span>
                        </WeekPreviewHeader>
                        {weekPreview.weeks.map((week, idx) => (
                          <WeekPreviewRow key={week.week} $isHoliday={week.isHoliday} $isEven={idx % 2 === 0}>
                            <span>W{week.week}</span>
                            <span>{week.startDate}</span>
                            <span>{week.endDate}</span>
                            <WeekPreviewBadge $isHoliday={week.isHoliday}>
                              {week.isHoliday ? 'Holiday' : 'Teaching'}
                            </WeekPreviewBadge>
                          </WeekPreviewRow>
                        ))}
                      </WeekPreviewContainer>
                      <WeekPreviewSummary>
                        <span>Total: {weekPreview.totalWeeks} weeks</span>
                        <span>Teaching: {weekPreview.totalTeachingWeeks} weeks</span>
                        <span>Holiday: {weekPreview.totalWeeks - weekPreview.totalTeachingWeeks} weeks</span>
                      </WeekPreviewSummary>
                    </S.FormGroup>
                  )}

                  <S.FormGroup>
                    <label>Schedule</label>
                    {formData.schedules.map((schedule, idx) => {
                      const scheduleSubjects = subjectIdsOf(schedule);
                      const availableTeachers = getTeachersForSubjects(scheduleSubjects, schedule);
                      const teacherNeed = {
                        dayOfWeek: schedule.dayOfWeek,
                        startTime: schedule.startTime,
                        endTime: schedule.endTime,
                        year: formData.year,
                        subjectIds: scheduleSubjects
                      };
                      return (
                        <div key={idx} style={{ marginBottom: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <S.FilterSelect
                              value={schedule.dayOfWeek}
                              onChange={(e) => updateSchedule(idx, 'dayOfWeek', e.target.value)}
                              style={{ flex: 1 }}
                            >
                              {DAY_OF_WEEK_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </S.FilterSelect>
                            <S.Input
                              type="time"
                              value={schedule.startTime}
                              onChange={(e) => updateSchedule(idx, 'startTime', e.target.value)}
                              style={{ width: '110px' }}
                            />
                            <span>-</span>
                            <S.Input
                              type="time"
                              value={schedule.endTime}
                              onChange={(e) => updateSchedule(idx, 'endTime', e.target.value)}
                              style={{ width: '110px' }}
                            />
                            {formData.schedules.length > 1 && (
                              <S.SmallButton color="#ef4444" onClick={() => removeSchedule(idx)}>
                                X
                              </S.SmallButton>
                            )}
                          </div>
                          {/* One block can cover more than one subject: a 90-minute
                              English-and-Maths session is one booking, not two. Toggling them
                              here is what used to require entering the class twice, which drew
                              two identical cards and a false room clash. */}
                          {flattenedSubjects.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                              {flattenedSubjects.map(subject => {
                                const on = scheduleSubjects.includes(subject.id);
                                return (
                                  <S.SmallButton
                                    key={subject.id}
                                    color={on ? '#F1AF59' : '#e5e7eb'}
                                    style={{ color: on ? 'white' : '#4b5563' }}
                                    onClick={() => toggleScheduleSubject(idx, subject.id)}
                                  >
                                    {on ? '✓ ' : ''}{subject.name.trim()}
                                  </S.SmallButton>
                                );
                              })}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {/* A teacher can only be picked from the ones assigned to the group,
                                which nothing has done yet while it is being created. */}
                            {isEdit && flattenedSubjects.length > 0 ? (
                              <S.FilterSelect
                                value={schedule.teacherId || ''}
                                onChange={(e) => updateSchedule(idx, 'teacherId', e.target.value)}
                                style={{ flex: 1 }}
                                disabled={!scheduleSubjects.length}
                              >
                                <option value="">-- Select Teacher --</option>
                                {availableTeachers.length > 0 ? (
                                  availableTeachers.map(t => {
                                    const name = `${t.teacherName}${t.isPrimary ? ' (Primary)' : ''}`;
                                    return (
                                      <option key={t.teacherId} value={t.teacherId}>
                                        {showAvailabilityHints
                                          ? teacherOptionLabel(t, teacherNeed, name)
                                          : name}
                                      </option>
                                    );
                                  })
                                ) : scheduleSubjects.length ? (
                                  <option value="" disabled>
                                    No teacher covers {scheduleSubjects.length > 1 ? 'all these subjects' : 'this subject'}
                                  </option>
                                ) : null}
                              </S.FilterSelect>
                            ) : null}
                            <S.Input
                              type="text"
                              list={`course-group-form-schedule-rooms-${idx}`}
                              placeholder="Room"
                              value={schedule.room || ''}
                              onChange={(e) => updateSchedule(idx, 'room', e.target.value)}
                              style={{ width: '120px' }}
                            />
                            <datalist id={`course-group-form-schedule-rooms-${idx}`}>
                              {rooms.map(r => <option key={r} value={r} />)}
                            </datalist>
                            {!isEdit && (
                              <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                                Teacher: after creation
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <S.SmallButton color="#10b981" onClick={addSchedule}>
                        + Add Time Slot
                      </S.SmallButton>
                      {isEdit && assignedTeachers.length > 0
                        && formData.schedules.some(s => subjectIdsOf(s).length && !s.teacherId) && (
                        <S.SmallButton color="#3b82f6" onClick={autoAssignTeachersToSchedules}>
                          Auto-Assign Teachers
                        </S.SmallButton>
                      )}
                    </div>
                  </S.FormGroup>
                </>
              )}

              {/* Special term slots */}
              {isSpecialTerm(formData.term) && (
                <S.FormGroup>
                  <label>Class Slots</label>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                    {isEdit
                      ? 'Manage individual class sessions for this holiday course.'
                      : 'Add individual class sessions for this holiday course. Each slot has its own date, time, subject, and teacher.'}
                  </p>
                  {formData.slots.length > 0 && (
                    <>
                      <SlotSelectionBar>
                        <SlotCheckboxLabel>
                          <input
                            type="checkbox"
                            checked={selectedSlotIndices.size === formData.slots.length && formData.slots.length > 0}
                            onChange={(e) => (e.target.checked ? selectAllSlots() : clearSlotSelection())}
                          />
                          Select All
                        </SlotCheckboxLabel>
                        {selectedSlotIndices.size > 0 && (
                          <S.SmallButton color="#6b7280" onClick={clearSlotSelection} style={{ padding: '4px 8px', fontSize: '12px' }}>
                            Clear
                          </S.SmallButton>
                        )}
                        <SlotSelectionCount>
                          {selectedSlotIndices.size > 0 ? `${selectedSlotIndices.size} selected` : ''}
                        </SlotSelectionCount>
                        <S.SmallButton
                          color="#8b5cf6"
                          onClick={copySelectedSlots}
                          disabled={selectedSlotIndices.size === 0}
                          style={{ padding: '4px 10px', fontSize: '12px', opacity: selectedSlotIndices.size === 0 ? 0.5 : 1 }}
                        >
                          Copy Selected (+1 day)
                        </S.SmallButton>
                      </SlotSelectionBar>
                      <SlotPreviewContainer>
                        <SlotPreviewHeader style={{ gridTemplateColumns: '24px 50px 120px 1fr 36px' }}>
                          <span></span>
                          <span>Slot</span>
                          <span>Date</span>
                          <span>Time</span>
                          <span></span>
                        </SlotPreviewHeader>
                        {formData.slots.map((slot, idx) => {
                          const slotTeachers = getTeachersForSubject(slot.subjectId);
                          return (
                            <SlotInputRow key={slot.slotIndex || idx} $isEven={idx % 2 === 0}>
                              <SlotInputMain style={{ gridTemplateColumns: '24px 50px 120px 1fr 36px' }}>
                                <SlotRowCheckbox
                                  type="checkbox"
                                  checked={selectedSlotIndices.has(idx)}
                                  onChange={() => toggleSlotSelection(idx)}
                                />
                                <SlotBadge>#{idx + 1}</SlotBadge>
                                <S.Input
                                  type="date"
                                  value={slot.date}
                                  min={formData.startDate}
                                  max={formData.finishDate}
                                  onChange={(e) => updateSlot(idx, 'date', e.target.value)}
                                  style={{ fontSize: '12px', padding: '6px' }}
                                />
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <S.Input
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) => updateSlot(idx, 'startTime', e.target.value)}
                                    style={{ width: '100px', fontSize: '12px', padding: '6px' }}
                                  />
                                  <span>-</span>
                                  <S.Input
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) => updateSlot(idx, 'endTime', e.target.value)}
                                    style={{ width: '100px', fontSize: '12px', padding: '6px' }}
                                  />
                                </div>
                                <S.SmallButton color="#ef4444" onClick={() => removeSlot(idx)} style={{ padding: '4px 8px' }}>
                                  X
                                </S.SmallButton>
                              </SlotInputMain>
                              <SlotInputSub style={{ paddingLeft: '86px' }}>
                                <S.FilterSelect
                                  value={slot.subjectId || ''}
                                  onChange={(e) => {
                                    updateSlot(idx, 'subjectId', e.target.value);
                                    if (isEdit) updateSlot(idx, 'teacherId', '');
                                  }}
                                  style={{ fontSize: '12px', padding: '6px' }}
                                >
                                  <option value="">-- Select Subject --</option>
                                  {flattenedSubjects.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                  ))}
                                </S.FilterSelect>
                                {isEdit ? (
                                  <S.FilterSelect
                                    value={slot.teacherId || ''}
                                    onChange={(e) => updateSlot(idx, 'teacherId', e.target.value)}
                                    style={{ fontSize: '12px', padding: '6px' }}
                                    disabled={!slot.subjectId}
                                  >
                                    <option value="">-- Select Teacher --</option>
                                    {slotTeachers.length > 0 ? (
                                      slotTeachers.map(t => (
                                        <option key={t.teacherId} value={t.teacherId}>
                                          {t.teacherName}{t.isPrimary ? ' (Primary)' : ''}
                                        </option>
                                      ))
                                    ) : slot.subjectId ? (
                                      <option value="" disabled>No teachers assigned</option>
                                    ) : null}
                                  </S.FilterSelect>
                                ) : null}
                                <S.Input
                                  type="text"
                                  list={`course-group-form-slot-rooms-${idx}`}
                                  placeholder="Room"
                                  value={slot.room || ''}
                                  onChange={(e) => updateSlot(idx, 'room', e.target.value)}
                                  style={{ width: '100px', fontSize: '12px', padding: '6px' }}
                                />
                                <datalist id={`course-group-form-slot-rooms-${idx}`}>
                                  {rooms.map(r => <option key={r} value={r} />)}
                                </datalist>
                                {!isEdit && (
                                  <span style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                                    Teacher: after creation
                                  </span>
                                )}
                              </SlotInputSub>
                            </SlotInputRow>
                          );
                        })}
                      </SlotPreviewContainer>
                    </>
                  )}
                  {formData.slots.length === 0 && (
                    <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center', color: '#6b7280' }}>
                      No slots added yet. Click below to add class sessions.
                    </div>
                  )}
                  <SlotPreviewSummary>
                    <span>Total slots: {formData.slots.length}</span>
                  </SlotPreviewSummary>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <S.SmallButton color="#8b5cf6" onClick={addSlot}>
                      + Add Class Slot
                    </S.SmallButton>
                    {isEdit && assignedTeachers.length > 0
                      && formData.slots.some(s => s.subjectId && !s.teacherId) && (
                      <S.SmallButton color="#3b82f6" onClick={autoAssignTeachersToSlots}>
                        Auto-Assign Teachers
                      </S.SmallButton>
                    )}
                  </div>
                </S.FormGroup>
              )}
            </>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                As an Admin, you can only edit the offset field.
              </p>
              <S.FormGroup>
                <label>Offset (days)</label>
                <S.Input
                  type="number"
                  value={formData.offset}
                  onChange={(e) => setFormData({ ...formData, offset: e.target.value })}
                />
                <small style={{ color: '#6b7280' }}>
                  Adjust this to shift the current week calculation forward or backward.
                </small>
              </S.FormGroup>
            </>
          )}

          {/* What the timetable has already done to this class. The fields above describe the
              rule; these are the weeks that do not follow it, and without them an admin editing
              "Mondays 17:45" cannot tell that next Monday was cancelled from another screen.
              Read-only apart from putting a week back. */}
          {lessonExceptions.length > 0 && (
            <S.FormGroup>
              <label>Changed weeks ({lessonExceptions.length})</label>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                Made on the timetable and applied to one date only, so they are not part of the
                schedule above. Restoring a week returns it to the schedule.
              </p>
              {lessonExceptions.map(lesson => {
                const movedTo = lesson.date && lesson.date !== lesson.originalDate ? lesson.date : '';
                return (
                  <div
                    key={lesson.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '9px 12px', marginBottom: '6px',
                      background: '#f9fafb', borderRadius: '8px', fontSize: '13px'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>
                        {lesson.originalDate}
                        {movedTo ? ` → ${movedTo}` : ''}
                        {lesson.startTime ? ` · ${lesson.startTime}–${lesson.endTime}` : ''}
                        {lesson.room ? ` · Room ${lesson.room}` : ''}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '12px' }}>
                        {getLessonStatusLabel(lesson.status)}
                        {lesson.editedBy ? ` · by ${lesson.editedBy}` : ''}
                        {lesson.notes ? ` · ${lesson.notes}` : ''}
                      </div>
                    </div>
                    <S.SmallButton
                      color="#6b7280"
                      onClick={() => handleRestoreException(lesson)}
                      disabled={saving}
                    >
                      Restore
                    </S.SmallButton>
                  </div>
                );
              })}
            </S.FormGroup>
          )}
        </S.ModalContent>
        <S.ModalFooter>
          <S.Button color="#6b7280" onClick={close}>Cancel</S.Button>
          <S.Button color="#F1AF59" onClick={handleSave} disabled={saving || loading}>
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create')}
          </S.Button>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
};

export default CourseGroupFormModal;
